import { Button, type ButtonProps, styled } from 'tamagui';

import { Text } from '@/components/text';
import { GoogleLogo } from '@/components/google-logo';
import { MICRO_TRANSITION, MICRO_TRANSITION_BORDER } from '@/theme/motion';
import { borderWidth } from '@/theme/tokens.generated';

// Google's own "Sign in with Google" branding guidelines specify the
// button's appearance directly (white/light background, specific
// border, the logo used unmodified, "Roboto Medium" or a comparable
// system sans) -- this isn't a design preference, it's part of using
// their mark. Deliberately does NOT reuse PrimaryButton's red/bordered
// "stamped" treatment the rest of this app uses everywhere else.
const StyledButton = styled(Button, {
  name: 'GoogleSignInButton',
  alignSelf: 'center',
  backgroundColor: '#ffffff',
  borderColor: '#747775',
  borderWidth: borderWidth.hairline,
  borderRadius: '$full',
  paddingHorizontal: '$3',
  paddingVertical: 0,
  height: 48,
  width: '100%',

  // `transition` lives INSIDE hoverStyle/pressStyle -- see
  // primary-button.tsx's doc comment (top-level `transition` crashes
  // at runtime with a raw CSS string; the pseudo-style's own
  // `transition` is the supported pass-through).
  hoverStyle: {
    backgroundColor: '#f8f9fa',
    transition: MICRO_TRANSITION,
  },
  pressStyle: {
    backgroundColor: '#f1f3f4',
    transition: MICRO_TRANSITION,
  },
  // Was missing entirely, same gap as PrimaryButton's (caught in the
  // same 2026-09-03 audit). This button intentionally uses Google's own
  // brand hex values throughout (see the top comment), not this app's
  // $borderColorFocus token -- #1f1f1f matches the button's own label
  // color, giving a clearly visible, high-contrast focus indicator
  // against the white background without introducing an off-brand tint
  // Google's own guidelines don't specify.
  focusStyle: {
    borderColor: '#1f1f1f',
    transition: MICRO_TRANSITION_BORDER,
  },
  disabledStyle: {
    opacity: 0.5,
  },
});

export function GoogleSignInButton({ children, ...props }: ButtonProps) {
  return (
    <StyledButton {...props} icon={<GoogleLogo />}>
      <Text fontFamily="$body" fontWeight="$medium" fontSize="$meta" lineHeight="$meta" color="#1f1f1f">
        {children}
      </Text>
    </StyledButton>
  );
}
